import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/apiClient";
import {
  ChartLineUp, Receipt, ClipboardText, Stack, ShoppingCart, Truck, Barricade,
  Users, Package, Buildings, SignOut, UserCircle, Storefront, Gear, Wrench, Toolbox, Bank, List, X
} from "@phosphor-icons/react";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: ChartLineUp },
  { section: "Sales" },
  { to: "/quotations", label: "Quotations", icon: ClipboardText },
  { to: "/sales-orders", label: "Sales Orders", icon: Stack },
  { to: "/invoices", label: "Tax Invoices", icon: Receipt },
  { to: "/repair-invoices", label: "Repair Invoices", icon: Wrench },
  { to: "/credit-notes", label: "Credit Notes", icon: ClipboardText },
  { to: "/delivery-challans", label: "Delivery Challans", icon: Truck },
  { to: "/gate-passes", label: "Gate Passes", icon: Barricade },
  { section: "Purchase" },
  { to: "/purchase-orders", label: "Purchase Orders", icon: ShoppingCart },
  { to: "/inward-bills", label: "Inward Bills (ITC)", icon: Receipt, adminOnly: true },
  { to: "/purchase-bill-upload", label: "Upload Bill (AI)", icon: Receipt, adminOnly: false },
  { to: "/reconciliation", label: "Bank Reconciliation (AI)", icon: Bank, adminOnly: false },
  { to: "/accounting", label: "Accounting (Tally)", icon: Bank, adminOnly: false },
  { to: "/debit-notes", label: "Debit Notes", icon: ClipboardText },
  { to: "/job-work-challans", label: "Job Work Challans", icon: Toolbox },
  { section: "Masters" },
  { to: "/customers", label: "Customers", icon: Users },
  { to: "/vendors", label: "Vendors", icon: Storefront },
  { to: "/products", label: "Products", icon: Package },
  { to: "/inventory", label: "Inventory", icon: Stack },
  { to: "/company", label: "Company Profile", icon: Buildings },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [company, setCompany] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    api.get("/company").then(r => setCompany(r.data || {})).catch((err) => console.error("Company load failed:", err));
  }, []);
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    nav("/login", { replace: true });
  };

  return (
    <div className="min-h-screen flex bg-surface text-ink" data-testid="app-shell">
      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-40 h-14 bg-white border-b border-border flex items-center gap-3 px-3 no-print" data-testid="mobile-topbar">
        <button onClick={() => setMenuOpen(o => !o)} className="btn-ghost !px-2" data-testid="mobile-menu-toggle" aria-label="Menu">{menuOpen ? <X size={22} /> : <List size={22} />}</button>
        <Link to="/dashboard" className="font-display font-bold truncate">{company?.trade_name || "Ramkishan Agri"}</Link>
      </div>
      {menuOpen && <div className="lg:hidden fixed inset-0 z-40 bg-ink/40" onClick={() => setMenuOpen(false)} data-testid="mobile-menu-backdrop" />}
      {/* Sidebar */}
      <aside className={`w-64 min-h-screen bg-surface-muted border-r border-border flex flex-col flex-shrink-0 no-print fixed lg:static inset-y-0 left-0 z-50 max-h-screen transition-transform duration-200 ${menuOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`} data-testid="app-sidebar">
        <div className="px-5 py-5 border-b border-border">
          <Link to="/dashboard" className="flex items-center gap-3" data-testid="brand-link">
            {company?.logo_url ? (
              <img src={company.logo_url} alt="logo" className="w-10 h-10 rounded-md object-cover border border-border bg-white" />
            ) : (
              <div className="w-10 h-10 rounded-md bg-brand text-white flex items-center justify-center font-display font-bold text-lg">R</div>
            )}
            <div className="leading-tight min-w-0">
              <div className="font-display font-bold text-[14px] text-ink tracking-tight truncate">{company?.trade_name || "Ramkishan Agri"}</div>
              <div className="text-[10px] text-ink-muted uppercase tracking-[0.14em]">Billing Suite</div>
            </div>
          </Link>
        </div>
        <nav className="flex-1 py-3 overflow-y-auto">
          {NAV.map((item, i) => {
            if (item.section) {
              return (
                <div key={"s-" + i} className="px-5 mt-4 mb-1 label-overline">{item.section}</div>
              );
            }
            if (item.adminOnly && user?.role !== "admin") return null;
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.to.replace("/", "")}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 mx-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-brand text-white"
                      : "text-ink-muted hover:text-brand hover:bg-surface-hover"
                  }`
                }
              >
                <Icon size={18} weight="duotone" />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
          {user?.role === "admin" && (
            <>
              <div className="px-5 mt-4 mb-1 label-overline">Admin</div>
              <NavLink
                to="/users"
                data-testid="nav-users"
                className={({ isActive }) =>
                  `flex items-center gap-3 mx-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive ? "bg-brand text-white" : "text-ink-muted hover:text-brand hover:bg-surface-hover"
                  }`
                }
              >
                <UserCircle size={18} weight="duotone" />
                <span>User Management</span>
              </NavLink>
              <NavLink
                to="/settings"
                data-testid="nav-settings"
                className={({ isActive }) =>
                  `flex items-center gap-3 mx-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive ? "bg-brand text-white" : "text-ink-muted hover:text-brand hover:bg-surface-hover"
                  }`
                }
              >
                <Gear size={18} weight="duotone" />
                <span>Settings</span>
              </NavLink>
              <NavLink
                to="/reports"
                data-testid="nav-reports-hub"
                className={({ isActive }) =>
                  `flex items-center gap-3 mx-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive ? "bg-brand text-white" : "text-ink-muted hover:text-brand hover:bg-surface-hover"
                  }`
                }
              >
                <ClipboardText size={18} weight="duotone" />
                <span>Customers · Tally Export</span>
              </NavLink>
              <NavLink to="/tally-import" data-testid="nav-tally-import"
                className={({ isActive }) => `flex items-center gap-3 mx-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${isActive ? "bg-brand text-white" : "text-ink-muted hover:text-brand hover:bg-surface-hover"}`}>
                <ClipboardText size={18} weight="duotone" />
                <span>Tally Import (AI)</span>
              </NavLink>
              <NavLink to="/audit" data-testid="nav-audit"
                className={({ isActive }) => `flex items-center gap-3 mx-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${isActive ? "bg-brand text-white" : "text-ink-muted hover:text-brand hover:bg-surface-hover"}`}>
                <Gear size={18} weight="duotone" />
                <span>AI Audit Alerts</span>
              </NavLink>
              <NavLink
                to="/reports/sales"
                data-testid="nav-sales-report"
                className={({ isActive }) =>
                  `flex items-center gap-3 mx-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive ? "bg-brand text-white" : "text-ink-muted hover:text-brand hover:bg-surface-hover"
                  }`
                }
              >
                <ChartLineUp size={18} weight="duotone" />
                <span>Sales Report</span>
              </NavLink>
              <NavLink to="/reports/outstanding" data-testid="nav-outstanding"
                className={({ isActive }) => `flex items-center gap-3 mx-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${isActive ? "bg-brand text-white" : "text-ink-muted hover:text-brand hover:bg-surface-hover"}`}>
                <ClipboardText size={18} weight="duotone" />
                <span>Outstanding Report</span>
              </NavLink>
              <NavLink to="/reports/payables" data-testid="nav-payables"
                className={({ isActive }) => `flex items-center gap-3 mx-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${isActive ? "bg-brand text-white" : "text-ink-muted hover:text-brand hover:bg-surface-hover"}`}>
                <ClipboardText size={18} weight="duotone" />
                <span>Payables (Vendors)</span>
              </NavLink>
              <NavLink to="/reports/stock-summary" data-testid="nav-stock-summary"
                className={({ isActive }) => `flex items-center gap-3 mx-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${isActive ? "bg-brand text-white" : "text-ink-muted hover:text-brand hover:bg-surface-hover"}`}>
                <Stack size={18} weight="duotone" />
                <span>Stock Summary</span>
              </NavLink>
              <NavLink
                to="/reports/gstr1"
                data-testid="nav-reports"
                className={({ isActive }) =>
                  `flex items-center gap-3 mx-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive ? "bg-brand text-white" : "text-ink-muted hover:text-brand hover:bg-surface-hover"
                  }`
                }
              >
                <ClipboardText size={18} weight="duotone" />
                <span>GSTR-1 Report</span>
              </NavLink>
              <NavLink
                to="/reports/gstr3b"
                data-testid="nav-reports-gstr3b"
                className={({ isActive }) =>
                  `flex items-center gap-3 mx-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive ? "bg-brand text-white" : "text-ink-muted hover:text-brand hover:bg-surface-hover"
                  }`
                }
              >
                <ClipboardText size={18} weight="duotone" />
                <span>GSTR-3B Report</span>
              </NavLink>
            </>
          )}
        </nav>
        <div className="px-4 py-4 border-t border-border">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-full bg-brand-secondary text-brand flex items-center justify-center font-semibold text-sm">
              {(user?.name || "U").slice(0,1).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate" data-testid="sidebar-user-name">{user?.name}</div>
              <div className="text-[11px] text-ink-muted uppercase tracking-wider" data-testid="sidebar-user-role">{user?.role}</div>
            </div>
          </div>
          <button
            data-testid="logout-button"
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-md border border-border text-sm font-medium text-ink-muted hover:text-brand hover:bg-white transition-colors"
          >
            <SignOut size={16} weight="bold" /> Logout
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 flex flex-col pt-14 lg:pt-0">
        <Outlet />
      </main>
    </div>
  );
}
